/**
 * Andar Bahar — the TABLE CLOCK seam (plan §6.0/§7). Same shape as
 * aviator/tableClock.ts: the Page derives every phase from `round()` and
 * nothing else; Phase D swaps `LocalTableClock` for `ServerTableClock`.
 *
 * Round: open (joker drawn into preroll, shown) → betting 12s → locked (the
 * deal is decided) → dealing (cards land at the cadence) → result → 3s
 * intermission → next round.
 */
import { type Card } from '../_cards/deck';
import { type Side, firstSideFor, payoutFor, winReturn, sampleK, dealFor, drawJoker, sideForK } from './engine';
import { mulberry32, mix } from '../aviator/prng';

export const WINDOW_MS = 12_000;
export const ARM_MS = 800;           // locked → first card
export const CARD_MS = 350;          // per card; after the 10th → FAST_MS
export const FAST_MS = 220;
export const RESULT_MS = 1_200;      // match burst → settle stamps
export const INTERMISSION_MS = 3_000;

export type RoundStatus = 'betting' | 'locked' | 'dealing' | 'result' | 'intermission';

export type Round = {
  no: number;
  status: RoundStatus;
  joker: Card;
  firstSide: Side;
  opensAt: number;
  locksAt: number;
  dealStartsAt: number;
  /** the dealt sequence; grows one card at a time during 'dealing' */
  dealt: Card[];
  /** known once status ≥ 'result' */
  k: number | null;
  winner: Side | null;
  settlesAt: number | null;
  nextOpensAt: number | null;
  fakeSeed: number;
};

export type Bet = { side: Side; amount: number; id: string };

export interface Wallet { balance(): number; debit(n: number): boolean; credit(n: number): void; }

export interface TableClock {
  serverNow(): number;
  round(): Round;
  myBets(): Bet[];
  history(): { winner: Side; k: number; first: Side }[];
  subscribe(cb: () => void): () => void;
  /** place a chip on a side (repeat taps add); refused after lock */
  bet(side: Side, amount: number): { ok: true } | { ok: false; reason: 'locked' | 'balance' };
  /** cancel EVERY un-locked bet of this round (one-shot per round, per plan §5.5) */
  unbet(): number;
  /** the round's settled outcome for me, once status ≥ 'result' */
  myOutcome(): { staked: number; returned: number; net: number } | null;
  /**
   * Is the balance on screen the REAL one? (2026-09-06)
   *
   * The practice clock funds itself, so it is true from the first frame. The server clock only
   * learns the player's balance when its first `/state` lands, and until then the Page would
   * otherwise show the seeded practice figure — a number the player does not have. On a
   * real-money table that is a lie, and worse, `bet()` gates affordability on it, so a player
   * could compose a stake the server then refuses for reasons the UI never showed. The Page
   * shows a placeholder and holds betting closed until this goes true.
   */
  walletReady(): boolean;
  destroy(): void;
}

export class LocalTableClock implements TableClock {
  private r: Round;
  private bets: Bet[] = [];
  private hist: { winner: Side; k: number; first: Side }[] = [];
  private subs = new Set<() => void>();
  private timer = 0;
  private rng: () => number;
  private plan: Card[] = [];            // the decided deal (secret until dealt)
  private nextCardAt = 0;
  private settled = false;
  private outcome: { staked: number; returned: number; net: number } | null = null;

  constructor(private tableSeed: number, private wallet: Wallet, firstRoundNo = 1) {
    this.rng = mulberry32(mix(tableSeed, 0xAB));
    for (let i = 0; i < 12; i++) { const j = drawJoker(this.rng); const k = sampleK(this.rng); this.hist.push({ winner: sideForK(k, j), k, first: firstSideFor(j) }); }
    this.r = this.open(firstRoundNo, performance.now() + 400);
    this.timer = window.setInterval(() => this.tick(), 100);
  }

  private open(no: number, opensAt: number): Round {
    const joker = drawJoker(this.rng);
    return {
      no, status: 'betting', joker, firstSide: firstSideFor(joker),
      opensAt, locksAt: opensAt + WINDOW_MS, dealStartsAt: opensAt + WINDOW_MS + ARM_MS,
      dealt: [], k: null, winner: null, settlesAt: null, nextOpensAt: null,
      fakeSeed: mix(this.tableSeed, no),
    };
  }

  serverNow() { return performance.now(); }
  round() { return this.r; }
  myBets() { return this.bets; }
  history() { return this.hist; }
  myOutcome() { return this.outcome; }
  /** Practice chips are funded locally, so the figure on screen is never a guess. */
  walletReady() { return true; }
  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }
  private emit() { this.subs.forEach((cb) => cb()); }

  private tick() {
    const now = this.serverNow(); const r = this.r; let changed = false;
    if (r.status === 'betting' && now >= r.locksAt) {
      r.status = 'locked';
      /* the decision happens AT LOCK, like the worker: k from the prior,
         concrete cards to match it. PHASE-2 SEAM: server result_secret */
      const k = sampleK(this.rng);
      this.plan = dealFor(r.joker, k, this.rng);
      changed = true;
    }
    if (r.status === 'locked' && now >= r.dealStartsAt) {
      r.status = 'dealing'; this.nextCardAt = now; changed = true;
    }
    if (r.status === 'dealing' && now >= this.nextCardAt) {
      if (r.dealt.length < this.plan.length) {
        r.dealt = [...r.dealt, this.plan[r.dealt.length]];
        this.nextCardAt = now + (r.dealt.length >= 10 ? FAST_MS : CARD_MS);
        changed = true;
      }
      if (r.dealt.length === this.plan.length) {
        r.status = 'result';
        r.k = this.plan.length;
        r.winner = sideForK(r.k, r.joker);
        r.settlesAt = now + RESULT_MS;
        r.nextOpensAt = now + RESULT_MS + INTERMISSION_MS;
        this.settle();
        this.hist = [{ winner: r.winner, k: r.k, first: r.firstSide }, ...this.hist].slice(0, 20);
        changed = true;
      }
    }
    if (r.status === 'result' && r.settlesAt != null && now >= r.settlesAt) { r.status = 'intermission'; changed = true; }
    if (r.status === 'intermission' && r.nextOpensAt != null && now >= r.nextOpensAt) {
      this.r = this.open(r.no + 1, now);
      this.bets = []; this.settled = false; this.outcome = null;
      changed = true;
    }
    if (changed) this.emit();
  }

  /* PHASE-2 SEAM: the worker settles per user; here the practice wallet */
  private settle() {
    if (this.settled) return; this.settled = true;
    const r = this.r; if (!r.winner) return;
    const x = payoutFor(r.winner, r.joker);
    let staked = 0, returned = 0;
    for (const b of this.bets) { staked += b.amount; if (b.side === r.winner) returned += winReturn(b.amount, x); }
    if (returned > 0) this.wallet.credit(returned);
    this.outcome = { staked, returned, net: returned - staked };
  }

  bet(side: Side, amount: number) {
    if (this.r.status !== 'betting') return { ok: false as const, reason: 'locked' as const };
    if (!this.wallet.debit(amount)) return { ok: false as const, reason: 'balance' as const };
    this.bets = [...this.bets, { side, amount, id: `${this.r.no}-${this.bets.length}` }];   // PHASE-2 SEAM: POST /andar_bahar/bet
    this.emit();
    return { ok: true as const };
  }

  unbet() {
    if (this.r.status !== 'betting' || this.bets.length === 0) return 0;
    const total = this.bets.reduce((s, b) => s + b.amount, 0);
    this.wallet.credit(total);                                     // PHASE-2 SEAM: POST /andar_bahar/unbet (one-shot per round)
    this.bets = [];
    this.emit();
    return total;
  }

  destroy() { window.clearInterval(this.timer); this.subs.clear(); }
}
